import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, FolderKanban, MessageSquare,
  Image as ImageIcon, FileText, Mail, Settings, LogOut,
  Plus, Edit, Trash2, Upload, Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  adminMe, adminLogin, adminLogout,
  adminGetContent, adminPutSection,
  adminListAdd, adminListUpdate, adminListDelete,
  adminUploadImage, adminUploadPdf, adminGetContacts, adminDeleteContact
} from "@/services/api";

// ── Reusable image-upload field ──────────────────────────────────
function ImageField({ value, onChange }) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await adminUploadImage(f);
      onChange(res.url);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://... or upload below" />
      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="text-sm" />
        {uploading && <span className="text-xs text-gray-500">Uploading...</span>}
      </div>
      {value && (
        <img src={value} alt="" className="h-24 w-auto rounded-md border object-cover" />
      )}
    </div>
  );
}

// ── Reusable PDF-upload field ───────────────────────────────────
function PdfField({ value, onChange }) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await adminUploadPdf(f);
      onChange(res.url);
      toast({ title: "PDF uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/reports/... or upload below" />
      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept="application/pdf" onChange={handleFile} className="text-sm" />
        {uploading && <span className="text-xs text-gray-500">Uploading...</span>}
      </div>
      {value && (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
          View PDF
        </a>
      )}
    </div>
  );
}

// ── Login screen ─────────────────────────────────────────────────
function LoginCard({ onLoggedIn }) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(email, password);
      toast({ title: "Welcome back" });
      onLoggedIn();
    } catch (err) {
      toast({ title: "Login failed", description: err.message || "Invalid credentials", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Chinar Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@chinarfoundation.org" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy}
              className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white">
              {busy ? "Signing in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Generic list editor ──────────────────────────────────────────
function ListEditor({ section, items, fields, onChanged }) {
  const { toast } = useToast();
  const [editingItem, setEditingItem] = useState(null);
  const [open, setOpen] = useState(false);

  const blank = fields.reduce((acc, f) => ({ ...acc, [f.key]: f.default ?? "" }), {});
  const startAdd = () => { setEditingItem({ ...blank, _new: true }); setOpen(true); };
  const startEdit = (item) => { setEditingItem({ ...item }); setOpen(true); };

  const save = async () => {
    try {
      if (editingItem._new) {
        const { _new, ...payload } = editingItem;
        await adminListAdd(section, payload);
        toast({ title: "Added" });
      } else {
        const { id, ...payload } = editingItem;
        await adminListUpdate(section, id, payload);
        toast({ title: "Updated" });
      }
      setOpen(false);
      onChanged();
    } catch (err) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await adminListDelete(section, id);
      toast({ title: "Deleted" });
      onChanged();
    } catch (err) {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={startAdd} className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              {fields.slice(0, 3).map(f => (
                <th key={f.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{f.label}</th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(items || []).map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-sm text-gray-900">{item.id}</td>
                {fields.slice(0, 3).map(f => (
                  <td key={f.key} className="px-4 py-3 text-sm text-gray-700 max-w-md">
                    {f.type === "image" && item[f.key]
                      ? <img src={item[f.key]} alt="" className="h-10 w-10 rounded object-cover" />
                      : <span className="line-clamp-2">{String(item[f.key] ?? "")}</span>}
                  </td>
                ))}
                <td className="px-4 py-3 text-sm space-x-2 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => startEdit(item)}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(item.id)}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {(!items || items.length === 0) && (
              <tr><td colSpan={fields.slice(0, 3).length + 2} className="text-center text-gray-400 py-8">No items yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem && editingItem._new ? "Add item" : "Edit item"}</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{f.label}</label>
                  {f.type === "textarea" && (
                    <Textarea rows={4} value={editingItem[f.key] ?? ""}
                      onChange={(e) => setEditingItem({ ...editingItem, [f.key]: e.target.value })}
                      placeholder={f.placeholder || ""} />
                  )}
                  {f.type === "image" && (
                    <ImageField value={editingItem[f.key]} onChange={(v) => setEditingItem({ ...editingItem, [f.key]: v })} />
                  )}
                  {f.type === "pdf" && (
                    <PdfField value={editingItem[f.key]} onChange={(v) => setEditingItem({ ...editingItem, [f.key]: v })} />
                  )}
                  {f.type === "number" && (
                    <Input type="number" value={editingItem[f.key] ?? ""}
                      onChange={(e) => setEditingItem({ ...editingItem, [f.key]: e.target.value === "" ? "" : Number(e.target.value) })}
                      placeholder={f.placeholder || ""} />
                  )}
                  {(f.type === "text" || !f.type) && (
                    <Input value={editingItem[f.key] ?? ""}
                      onChange={(e) => setEditingItem({ ...editingItem, [f.key]: e.target.value })}
                      placeholder={f.placeholder || ""} />
                  )}
                  {f.help && <p className="text-xs text-gray-500 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Site settings (single object) ────────────────────────────────
function SiteSettings({ site, onChanged }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState(site || {});
  useEffect(() => setDraft(site || {}), [site]);

  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const setSocial = (k, v) => setDraft({ ...draft, socials: { ...(draft.socials || {}), [k]: v } });
  const setStory = (k, v) => setDraft({ ...draft, story: { ...(draft.story || {}), [k]: v } });
  const setRating = (key, k, v) => setDraft({
    ...draft,
    ratings: { ...(draft.ratings || {}), [key]: { ...((draft.ratings || {})[key] || {}), [k]: v } }
  });

  const save = async () => {
    try {
      await adminPutSection("site", draft);
      toast({ title: "Site settings saved" });
      onChanged();
    } catch (err) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader><CardTitle>Brand &amp; Tagline</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Brand name</label>
            <Input value={draft.brandName || ""} onChange={(e) => set("brandName", e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tagline (footer)</label>
            <Textarea rows={2} value={draft.tagline || ""} onChange={(e) => set("tagline", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><label className="block text-sm font-medium mb-1">Address</label>
            <Input value={draft.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Phone</label>
              <Input value={draft.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><label className="block text-sm font-medium mb-1">Toll Free</label>
              <Input value={draft.tollFree || ""} onChange={(e) => set("tollFree", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Email</label>
              <Input value={draft.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className="block text-sm font-medium mb-1">Support email</label>
              <Input value={draft.supportEmail || ""} onChange={(e) => set("supportEmail", e.target.value)} /></div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Office hours (one per line)</label>
            <Textarea rows={3}
              value={(draft.officeHours || []).join("\n")}
              onChange={(e) => set("officeHours", e.target.value.split("\n").map(x => x.trim()).filter(Boolean))} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Social links</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {["facebook", "twitter", "instagram", "youtube"].map(k => (
            <div key={k}>
              <label className="block text-sm font-medium mb-1 capitalize">{k}</label>
              <Input value={(draft.socials || {})[k] || ""} onChange={(e) => setSocial(k, e.target.value)} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Home page "Our Story" block</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><label className="block text-sm font-medium mb-1">Badge</label>
            <Input value={(draft.story || {}).badge || ""} onChange={(e) => setStory("badge", e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">Title</label>
            <Input value={(draft.story || {}).title || ""} onChange={(e) => setStory("title", e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">Paragraph 1</label>
            <Textarea rows={3} value={(draft.story || {}).paragraph1 || ""} onChange={(e) => setStory("paragraph1", e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">Paragraph 2</label>
            <Textarea rows={3} value={(draft.story || {}).paragraph2 || ""} onChange={(e) => setStory("paragraph2", e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">Embedded video URL (YouTube embed)</label>
            <Input value={(draft.story || {}).videoUrl || ""} onChange={(e) => setStory("videoUrl", e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Review badges</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {["google", "facebook"].map(key => (
            <div key={key} className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">{key} rating</label>
                <Input value={((draft.ratings || {})[key] || {}).rating || ""} onChange={(e) => setRating(key, "rating", e.target.value)} /></div>
              <div><label className="block text-sm font-medium mb-1">{key} count text</label>
                <Input value={((draft.ratings || {})[key] || {}).count || ""} onChange={(e) => setRating(key, "count", e.target.value)} /></div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
        <Save className="w-4 h-4 mr-2" /> Save site settings
      </Button>
    </div>
  );
}

// ── About-page editor (mission/vision/journey/values) ────────────
function AboutEditor({ about, onChanged }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState(about || {});
  useEffect(() => setDraft(about || {}), [about]);

  const setStory = (k, v) => setDraft({ ...draft, story: { ...(draft.story || {}), [k]: v } });

  const save = async () => {
    try {
      await adminPutSection("about", draft);
      toast({ title: "About page saved" });
      onChanged();
    } catch (err) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    }
  };

  const journey = draft.journey || [];
  const values = draft.coreValues || [];

  const addJourney = () => setDraft({ ...draft, journey: [...journey, { year: "", title: "", description: "" }] });
  const editJourney = (i, k, v) => {
    const next = [...journey]; next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, journey: next });
  };
  const removeJourney = (i) => setDraft({ ...draft, journey: journey.filter((_, idx) => idx !== i) });

  const addValue = () => setDraft({ ...draft, coreValues: [...values, { icon: "Heart", title: "", description: "" }] });
  const editValue = (i, k, v) => {
    const next = [...values]; next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, coreValues: next });
  };
  const removeValue = (i) => setDraft({ ...draft, coreValues: values.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader><CardTitle>Story header</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Badge" value={(draft.story || {}).badge || ""} onChange={(e) => setStory("badge", e.target.value)} />
          <Input placeholder="Title" value={(draft.story || {}).title || ""} onChange={(e) => setStory("title", e.target.value)} />
          <Textarea rows={3} placeholder="Intro paragraph" value={(draft.story || {}).intro || ""} onChange={(e) => setStory("intro", e.target.value)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Mission &amp; Vision</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Textarea rows={4} placeholder="Mission" value={draft.mission || ""} onChange={(e) => setDraft({ ...draft, mission: e.target.value })} />
          <Textarea rows={4} placeholder="Vision" value={draft.vision || ""} onChange={(e) => setDraft({ ...draft, vision: e.target.value })} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Journey milestones</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {journey.map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start">
              <Input className="col-span-2" placeholder="Year" value={m.year} onChange={(e) => editJourney(i, "year", e.target.value)} />
              <Input className="col-span-3" placeholder="Title" value={m.title} onChange={(e) => editJourney(i, "title", e.target.value)} />
              <Input className="col-span-6" placeholder="Description" value={m.description} onChange={(e) => editJourney(i, "description", e.target.value)} />
              <Button className="col-span-1" variant="outline" onClick={() => removeJourney(i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="outline" onClick={addJourney}><Plus className="w-4 h-4 mr-2" /> Add milestone</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Core values</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {values.map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start">
              <Input className="col-span-2" placeholder="Icon (lucide name)" value={v.icon} onChange={(e) => editValue(i, "icon", e.target.value)} />
              <Input className="col-span-3" placeholder="Title" value={v.title} onChange={(e) => editValue(i, "title", e.target.value)} />
              <Input className="col-span-6" placeholder="Description" value={v.description} onChange={(e) => editValue(i, "description", e.target.value)} />
              <Button className="col-span-1" variant="outline" onClick={() => removeValue(i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="outline" onClick={addValue}><Plus className="w-4 h-4 mr-2" /> Add value</Button>
        </CardContent>
      </Card>

      <Button onClick={save} className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
        <Save className="w-4 h-4 mr-2" /> Save about page
      </Button>
    </div>
  );
}

// ── Hero image (single value) ────────────────────────────────────
function HeroImageEditor({ heroImage, onChanged }) {
  const { toast } = useToast();
  const [val, setVal] = useState(heroImage || "");
  useEffect(() => setVal(heroImage || ""), [heroImage]);

  const save = async () => {
    try {
      await adminPutSection("heroImage", val);
      toast({ title: "Hero image saved" });
      onChanged();
    } catch (err) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <Card>
        <CardHeader><CardTitle>Home page hero image</CardTitle></CardHeader>
        <CardContent>
          <ImageField value={val} onChange={setVal} />
        </CardContent>
      </Card>
      <Button onClick={save} className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
        <Save className="w-4 h-4 mr-2" /> Save
      </Button>
    </div>
  );
}

// ── Contact submissions viewer ───────────────────────────────────
function ContactsView() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminGetContacts()); }
    catch (e) { toast({ title: "Could not load", description: e.message, variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await adminDeleteContact(id);
    load();
  };

  if (loading) return <p className="text-gray-500">Loading...</p>;
  if (rows.length === 0) return (
    <Card><CardContent className="p-12 text-center">
      <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
      <p className="text-gray-500">No contact submissions yet.</p>
    </CardContent></Card>
  );

  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <Card key={r.id}>
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-semibold text-gray-900">{r.name} <span className="text-sm font-normal text-gray-500">&lt;{r.email}&gt;</span></p>
                <p className="text-sm text-gray-500">{new Date(r.createdAt).toLocaleString()} {r.phone ? `· ${r.phone}` : ""}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <p className="font-medium text-gray-800 mb-1">{r.subject}</p>
            <p className="text-gray-600 whitespace-pre-line">{r.message}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────
const AdminPanel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("overview");

  const refresh = useCallback(async () => {
    try { setContent(await adminGetContent()); }
    catch (e) {
      if (e.status === 401) setAuthed(false);
      else toast({ title: "Could not load content", description: e.message, variant: "destructive" });
    }
  }, [toast]);

  useEffect(() => {
    (async () => {
      try {
        const me = await adminMe();
        setAuthed(!!me.authenticated);
      } catch (e) { setAuthed(false); }
      finally { setAuthChecked(true); }
    })();
  }, []);

  useEffect(() => { if (authed) refresh(); }, [authed, refresh]);

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  }
  if (!authed) {
    return <LoginCard onLoggedIn={() => setAuthed(true)} />;
  }

  const handleLogout = async () => {
    await adminLogout();
    setAuthed(false);
    navigate("/");
  };

  const NAV = [
    { key: "overview",     label: "Overview",        icon: LayoutDashboard },
    { key: "site",         label: "Site Settings",   icon: Settings },
    { key: "heroSlides",   label: "Hero Slides",     icon: ImageIcon },
    { key: "heroImage",    label: "Hero Image",      icon: ImageIcon },
    { key: "services",     label: "Services",        icon: Briefcase },
    { key: "stats",        label: "Stats",           icon: LayoutDashboard },
    { key: "projects",     label: "Projects",        icon: FolderKanban },
    { key: "testimonials", label: "Testimonials",    icon: MessageSquare },
    { key: "featuredLogos",label: "Featured Logos",  icon: ImageIcon },
    { key: "annualReports",label: "Annual Reports",  icon: FileText },
    { key: "about",        label: "About Page",      icon: FileText },
    { key: "contacts",     label: "Contact Inbox",   icon: Mail }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-gray-900 text-white overflow-y-auto">
        <div className="p-6">
          <h1 className="text-2xl font-bold">Chinar Admin</h1>
          <p className="text-sm text-gray-400 mt-1">Management Panel</p>
        </div>
        <nav className="mt-2 pb-24">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`w-full flex items-center space-x-3 px-6 py-3 hover:bg-gray-800 transition-colors ${
                tab === key ? "bg-gray-800 border-l-4 border-blue-500" : ""
              }`}>
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full p-4 bg-gray-900">
          <Button onClick={handleLogout} variant="outline" className="w-full border-gray-700 text-white hover:bg-gray-800">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-64 p-8">
        {!content ? (
          <p className="text-gray-500">Loading content...</p>
        ) : (
          <>
            {tab === "overview" && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Dashboard Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    ["Services", (content.services || []).length, Briefcase, "text-blue-600"],
                    ["Projects", (content.projects || []).length, FolderKanban, "text-green-600"],
                    ["Testimonials", (content.testimonials || []).length, MessageSquare, "text-purple-600"],
                    ["Hero Slides", (content.heroSlides || []).length, ImageIcon, "text-yellow-600"]
                  ].map(([label, value, Icon, color]) => (
                    <Card key={label}>
                      <CardContent className="p-6 flex justify-between items-start">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{label}</p>
                          <p className="text-3xl font-bold text-gray-900">{value}</p>
                        </div>
                        <Icon className={`w-8 h-8 ${color}`} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="mt-8">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">Tips</h3>
                    <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                      <li>Use the left sidebar to edit each section of the website.</li>
                      <li>Image fields support direct URLs or upload to Cloudinary.</li>
                      <li>Contact form submissions appear under "Contact Inbox".</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}

            {tab === "site" && <SiteSettings site={content.site} onChanged={refresh} />}

            {tab === "heroImage" && <HeroImageEditor heroImage={content.heroImage} onChanged={refresh} />}

            {tab === "heroSlides" && (
              <ListEditor section="heroSlides" items={content.heroSlides} onChanged={refresh}
                fields={[
                  { key: "title", label: "Title" },
                  { key: "subtitle", label: "Subtitle", type: "textarea" },
                  { key: "impactText", label: "Impact text" }
                ]} />
            )}

            {tab === "services" && (
              <ListEditor section="services" items={content.services} onChanged={refresh}
                fields={[
                  { key: "title", label: "Title" },
                  { key: "description", label: "Description", type: "textarea" },
                  { key: "icon", label: "Icon (lucide name, e.g. Heart, Stethoscope)", help: "See lucide.dev for the full list" },
                  { key: "slug", label: "Slug" }
                ]} />
            )}

            {tab === "stats" && (
              <ListEditor section="stats" items={content.stats} onChanged={refresh}
                fields={[
                  { key: "label", label: "Label" },
                  { key: "value", label: "Value", type: "number" },
                  { key: "icon", label: "Icon (lucide name)" }
                ]} />
            )}

            {tab === "projects" && (
              <ListEditor section="projects" items={content.projects} onChanged={refresh}
                fields={[
                  { key: "title", label: "Title" },
                  { key: "description", label: "Description", type: "textarea" },
                  { key: "image", label: "Image", type: "image" },
                  { key: "status", label: "Status", help: "'In Progress' or 'Completed'" },
                  { key: "progress", label: "Progress (0-100)", type: "number" }
                ]} />
            )}

            {tab === "testimonials" && (
              <ListEditor section="testimonials" items={content.testimonials} onChanged={refresh}
                fields={[
                  { key: "name", label: "Name" },
                  { key: "text", label: "Text", type: "textarea" },
                  { key: "avatar", label: "Avatar", type: "image" },
                  { key: "platform", label: "Platform", help: "Google / Facebook / etc." },
                  { key: "rating", label: "Rating (1-5)", type: "number", default: 5 }
                ]} />
            )}

            {tab === "featuredLogos" && (
              <ListEditor section="featuredLogos" items={content.featuredLogos} onChanged={refresh}
                fields={[
                  { key: "name", label: "Name" },
                  { key: "url", label: "Logo image", type: "image" }
                ]} />
            )}

            {tab === "annualReports" && (
              <ListEditor section="annualReports" items={content.annualReports} onChanged={refresh}
                fields={[
                  { key: "year", label: "Year", type: "number" },
                  { key: "title", label: "Title" },
                  { key: "pdfUrl", label: "PDF File", type: "pdf" },
                  { key: "thumbnail", label: "Thumbnail", type: "image" }
                ]} />
            )}

            {tab === "about" && <AboutEditor about={content.about} onChanged={refresh} />}

            {tab === "contacts" && <ContactsView />}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
